import {
	DriverScheduleTripTypeEnum,
	NormalizedScheduledLeg,
} from '@mopla/data-models';
import { diffInShownMinutes } from '@mopla/utils';

import { ScheduleDiffEl, ScheduleDiffTypes } from '../../entities/scheduleDiff';
import { getEffectiveLegTimes, getLegTimeDeviation } from '../legTimes';

export type TScheduleDiffUpdate = {
	upsert: ScheduleDiffEl[];
	remove: string[];
};

const delayTypes: ScheduleDiffTypes[] = [
	ScheduleDiffTypes.DELAYED,
	ScheduleDiffTypes.DELAYED_BREAK,
];

const isDone = (leg: NormalizedScheduledLeg): boolean =>
	leg.state === 'DONE' || leg.state === 'DONE_BREAK';

const getDelayType = (
	leg: NormalizedScheduledLeg
): ScheduleDiffTypes | null => {
	if (!getLegTimeDeviation(leg)) {
		return null;
	}
	if (leg.ref === DriverScheduleTripTypeEnum.LEG) {
		return ScheduleDiffTypes.DELAYED;
	}
	if (leg.ref === DriverScheduleTripTypeEnum.BREAK) {
		return ScheduleDiffTypes.DELAYED_BREAK;
	}
	return null;
};

/** Whether the times the driver reads moved between two fetches. */
const haveTimesChanged = (
	prev: NormalizedScheduledLeg,
	next: NormalizedScheduledLeg
): boolean => {
	const before = getEffectiveLegTimes(prev);
	const after = getEffectiveLegTimes(next);

	return Boolean(
		diffInShownMinutes(before.effectiveStart, after.effectiveStart) ||
			diffInShownMinutes(before.effectiveEnd, after.effectiveEnd)
	);
};

/** Schedule changes since the previous fetch, merged into the unseen diff. */
export const buildScheduleDiff = (
	prevLegs: NormalizedScheduledLeg[],
	nextLegs: NormalizedScheduledLeg[],
	unseenDiff: ScheduleDiffEl[]
): TScheduleDiffUpdate => {
	const prevById = new Map(prevLegs.map((l) => [l.scheduledLegId, l]));
	const nextIds = new Set(nextLegs.map((l) => l.scheduledLegId));
	const unseenById = new Map(unseenDiff.map((d) => [d.scheduledLegId, d]));
	const result = new Map<string | undefined, ScheduleDiffEl>();
	const doneIds = new Set<string | undefined>();

	nextLegs.forEach((l) => {
		if (isDone(l)) {
			doneIds.add(l.scheduledLegId);
			return;
		}
		if (prevById.has(l.scheduledLegId)) {
			return;
		}

		if (l.ref === DriverScheduleTripTypeEnum.LEG) {
			result.set(l.scheduledLegId, {
				...l,
				updateState: ScheduleDiffTypes.NEW,
			});
		}

		/** Break id is its planned start, so a moved break is new as well. */
		if (l.ref === DriverScheduleTripTypeEnum.BREAK) {
			result.set(l.scheduledLegId, {
				...l,
				updateState: ScheduleDiffTypes.NEW_BREAK,
			});
		}
	});

	prevLegs.forEach((l) => {
		/** A finished leg leaving the schedule is not a cancellation. */
		if (!nextIds.has(l.scheduledLegId) && !isDone(l)) {
			result.set(l.scheduledLegId, {
				...l,
				updateState: ScheduleDiffTypes.DELETED,
			});
		}
	});

	nextLegs.forEach((l) => {
		if (result.has(l.scheduledLegId) || doneIds.has(l.scheduledLegId)) {
			return;
		}

		const unseen = unseenById.get(l.scheduledLegId);
		const prev = prevById.get(l.scheduledLegId);
		const delayType = getDelayType(l);
		const isUnseenDelay = !!unseen && delayTypes.includes(unseen.updateState);

		/** Unseen non-delay entry keeps its category, shows current times. */
		if (unseen && !isUnseenDelay) {
			result.set(l.scheduledLegId, { ...l, updateState: unseen.updateState });
		} else if (
			delayType &&
			(isUnseenDelay || (prev && haveTimesChanged(prev, l)))
		) {
			result.set(l.scheduledLegId, { ...l, updateState: delayType });
		}
	});

	/** Drop unseen entries that no longer hold: back on plan, or already done. */
	const remove = unseenDiff
		.filter(
			(d) =>
				!result.has(d.scheduledLegId) &&
				(delayTypes.includes(d.updateState) || doneIds.has(d.scheduledLegId))
		)
		.map((d) => d.scheduledLegId as string);

	return { upsert: [...result.values()], remove };
};
